import graphql__0a100b613bda53ca345c from "./__generated__/useDittoAppConnectionQuery.graphql.ts";import { Ditto } from '@dittolive/ditto'
import { useDitto } from '@dittolive/react-ditto'
import { useLogSpan } from '@portal/components'
import graphql from 'babel-plugin-relay/macro'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useLazyLoadQuery } from 'react-relay/hooks'

import { DITTO_PORTAL_APP } from '../../scenes/appManagement/store/DittoIssuesProvider'
import { useDittoAppConnectionQuery } from './__generated__/useDittoAppConnectionQuery.graphql'

const query = graphql__0a100b613bda53ca345c

type CloudDomain = { readonly url: string }

const DITTO_V4_RELEASE_DATE = new Date('2023-03-22')

/** Hook for connecting to a ditto app from the portal. */
const useDittoAppConnection = (
  appId: string,
  shouldConnect = true,
): {
  ditto: Ditto | null
  error: Error | undefined
  cloudDomain: CloudDomain | undefined
} => {
  const isPortalApp = appId === DITTO_PORTAL_APP
  const windowEnvironment = window._env_

  const { logSpan } = useLogSpan()
  const { viewer } = useLazyLoadQuery<useDittoAppConnectionQuery>(query, {
    appId,
    skip: isPortalApp,
  })

  // Will only load ditto if the hook is rendered with the shouldConnect value to true.
  const { ditto, error } = useDitto(shouldConnect ? appId : undefined)

  const cloudUrls = isPortalApp
    ? [
        {
          url: windowEnvironment.PORTAL_DITTO_APP_HYDRA_WEBSOCKET_URL || '',
        },
      ]
    : viewer.appById?.cloudUrls || []
  const cloudDomain = cloudUrls.length ? cloudUrls[0] : undefined

  const v3Override = viewer?.appById?.hydraCluster?.sdkVersion === '3'
  const appCreatedAfterV4 = dayjs(viewer?.appById?.createdAt).isAfter(
    DITTO_V4_RELEASE_DATE,
  )
  // If the app was created before v4, we should not disable v3 syncing.
  // If we have specified that we want to use v3, via sdkVersion, we should honor
  // that and not disable v3 syncing.
  const disableV3 = !v3Override && appCreatedAfterV4

  useEffect(
    function manageAppSyncing() {
      if (ditto && cloudDomain) {
        logSpan('app-connection-start-sync', () => {
          if (disableV3) {
            ditto.disableSyncWithV3()
          }

          ditto
            .updateTransportConfig((currentConfig) => {
              currentConfig.connect.websocketURLs = [`wss://${cloudDomain.url}`]
            })
            .startSync()
        })
      }
    },
    [ditto, cloudDomain, appId, logSpan, disableV3],
  )

  return { ditto, error, cloudDomain }
}

export default useDittoAppConnection
